/**
 * The console's composition root.
 *
 * Everything that knows about the outside world is decided here and nowhere else: which
 * gateway is wired, which base URL it talks to, and what the query cache's policy is. No
 * screen constructs a client; no component reads `import.meta.env`. That is the same shape
 * `apps/api/src/server.ts` uses, for the same reason — it is what lets every suite mount a
 * real screen against an in-memory gateway with no network and no mocking.
 *
 * Configuration is validated at import and throws on a missing value (CLAUDE.md §3), so a
 * console built without an API base URL fails loudly on the first paint instead of quietly
 * calling itself.
 */
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import "@wetalk/ui/broadsheet.css";
import "@wetalk/ui/wetalk-theme.css";
import "@wetalk/ui/wetalk-tokens.css";
import "@wetalk/ui/components.css";
import "@wetalk/ui/select.css";
import "@wetalk/ui/console-theme.css";
import "@wetalk/ui/console.css";
import "./styles/console.css";
import "./shell/shell.css";
import "./shell/shell-public-design.css";

import { apiGateway } from "./api/api-gateway.js";
import { GatewayProvider } from "./api/provider.js";
import { readConsoleConfig, type ConsoleConfig } from "./config.js";
import { buildRouter } from "./router.js";
import { browserStaleBuildReload, reloadOnceForStaleBuild } from "./stale-build.js";
import { isHelpChunkFailure, isUnnamedChunkFailure, reloadIfStaleBuild } from "./help/help-chunk.js";
import { initializePortalLocale, PortalLanguageBoundary } from "./i18n/language-control.js";
import { ThemeProvider } from "./shell/theme.js";
import type { ConsoleGateway } from "./api/gateway.js";
import { IconProvider } from "@wetalk/ui";

// A tab still on the build before a deploy asks for chunks the deploy removed: load the current build, once. A help
// chunk that did not load is not a stale build (a content blocker refused it): it is left to reject, and the help
// loader shows the widget as unavailable (`./help/help-chunk.ts`; plan-019 E2E fix, AC-HA-1).
globalThis.addEventListener("vite:preloadError", (event) => {
  if (isHelpChunkFailure(event.payload)) {
    // Safari names no file, so this may be another chunk's failure while a help import was in flight: a changed build
    // still reloads (a named help chunk's own loader checks the same, `loadHelpChunk`). plan-019 REVIEW-FIX.
    if (isUnnamedChunkFailure(event.payload)) void reloadIfStaleBuild();
    return;
  }
  if (reloadOnceForStaleBuild(browserStaleBuildReload())) event.preventDefault();
});

const config = readConsoleConfig(import.meta.env);

const gateway: ConsoleGateway = await gatewayFor(config);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A console showing a stale figure is worse than one that refetches: every number on
      // these screens is money or a live conversation.
      staleTime: 5_000,
      // View objects contain locale-aware display getters; retain them when caching a read.
      structuralSharing: false,
      retry: 1,
      refetchOnWindowFocus: true,
    },
  },
});

await initializePortalLocale();

const router = buildRouter({ gateway, queryClient, consoleVersion: config.consoleVersion });

const host = globalThis.document.getElementById("console-root");
if (host === null) throw new Error("index.html is missing #console-root.");

createRoot(host).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <GatewayProvider gateway={gateway}>
        <PortalLanguageBoundary>
        <ThemeProvider>
          <IconProvider>
            <RouterProvider router={router} />
          </IconProvider>
        </ThemeProvider>
        </PortalLanguageBoundary>
      </GatewayProvider>
    </QueryClientProvider>
  </StrictMode>,
);

/**
 * The gateway, chosen AT BUILD TIME (design §15.10.1; register finding: the production console
 * used to bundle the sandbox and pick at runtime).
 *
 * `import.meta.env.VITE_WETALK_GATEWAY` is replaced by its literal value when Vite builds, so in
 * an `api` build the condition below is the constant `"api" === "sandbox"`, the branch is dead,
 * and the bundler drops the dynamic `import()` with it: the sandbox gateway and every fixture
 * module it pulls in are not in the output at all. `src/gateway-selection.test.ts` builds the
 * console for `api` and asserts none of the sandbox's marker strings reach a chunk.
 *
 * The validated `config.gateway` must agree with the literal the bundler saw; they are the same
 * variable, so a disagreement is a broken build and is thrown rather than resolved.
 */
async function gatewayFor(validated: ConsoleConfig): Promise<ConsoleGateway> {
  if (import.meta.env.VITE_WETALK_GATEWAY === "sandbox") {
    if (validated.gateway !== "sandbox") throw new Error("VITE_WETALK_GATEWAY changed after the build.");
    const { SANDBOX_TIMING, sandboxGateway } = await import("./api/sandbox-gateway.js");
    return sandboxGateway(SANDBOX_TIMING);
  }
  if (validated.gateway !== "api") throw new Error("VITE_WETALK_GATEWAY changed after the build.");
  return apiGateway({
    baseUrl: validated.apiBaseUrl,
    // The console is the cookie-authenticated client (the spec's `app.wetalk.io`
    // server). The CSRF token is derived by the API from the auth_session cookie and
    // is never guessed: the gateway learns it from every auth_session body the API
    // returns (sign-up, log-in, `GET /v1/session`) and forgets it on log-out. A page
    // served with one in `<meta name="wetalk-csrf-token">` starts with it; a visitor
    // who is not signed in has none, and that is not an error.
    personCredential: { kind: "cookie", csrfToken: csrfTokenFromDocument() },
    fetch: globalThis.fetch.bind(globalThis),
    publicSiteBaseUrl: validated.publicSiteBaseUrl,
  });
}

/**
 * The double-submit CSRF token, when the document was served with one.
 *
 * `null` is a real state, not a missing value: a visitor who is not signed in has no
 * auth_session and so no token, and a sign-in inside this page mints a new one anyway. The
 * gateway learns the live token from the API's auth_session bodies; an authenticated write
 * sent without one is refused by the API (`csrf_token_invalid`), never silently accepted.
 */
function csrfTokenFromDocument(): string | null {
  const meta = globalThis.document.querySelector('meta[name="wetalk-csrf-token"]');
  const value = meta?.getAttribute("content");
  if (value === null || value === undefined || value === "") return null;
  return value;
}
